<!-- eslint-disable vue/no-mutating-props -->
<template>
    <div class="complaint--form">
        <div class="complaint--form-title">
            {{ edit ? "Minőségi reklamációs űrlap szerkesztése" : "Minőségi reklamáció bejelentő űrlap" }}
        </div>
        <div class="complaint--form-links">
            <a
                class="complaint--form-link"
                target="_blank"
                href="https://www.bmigroup.com/hu/szolgaltatasok/garancialis-feltetelek/"
            >
                Garanciális feltételek megtekintése
            </a>
            <a
                class="complaint--form-link"
                target="_blank"
                href="https://dxb-hungary.payloadcms.app/api/document/file/BMI_ASZF_2024_marc_4_OK.pdf"
            >
                Általános Szállítási Feltételek
            </a>
        </div>
        <div class="complaint--form-datas">
            <div class="complaint--form-subtitle">
                Reklamáció bejelentő adatai
            </div>

            <div class="complaint--form-input" :class="{'error' : validation.name}">
                <label
                    class="text-input-label"
                    for="name">
                    Név *
                </label>
                <input :disabled="disabled || loading" v-model="form.name" id="name" type="text">
                <span class="input-error">{{ validation.name }}</span>
            </div>

            <div class="complaint--form-input" :class="{'error' : validation.phone}">
                <label
                    class="text-input-label"
                    for="phone">
                    Telefonszám *
                </label>
                <input
                    v-model="form.phone"
                    :disabled="disabled || loading"
                    v-mask="{mask: '+36 99/999 9999', greedy: false, autoUnmask: true}"
                    id="phone"
                    type="text">
                <span class="input-error">{{ validation.phone }}</span>
            </div>

            <div class="complaint--form-input" :class="{'error' : validation.email}">
                <label
                    class="text-input-label"
                    for="email">
                    E-mail cím *
                </label>
                <input :disabled="disabled || loading" v-model="form.email" id="email" type="email">
                <span class="input-error">{{ validation.email }}</span>
            </div>

            <div class="complaint--form-input" :class="{'error' : validation.buyer_name}">
                <label
                    class="text-input-label"
                    for="buyer_name">
                    Vevő neve (ha nem azonos a reklamáció bejelentőjével)
                </label>
                <input :disabled="disabled || loading" v-model="form.buyer_name" id="buyer_name" type="string">
                <span class="input-error">{{ validation.buyer_name }}</span>
            </div>

            <div class="complaint--form-subtitle">
                Reklamáció helyszínének pontos címe:
            </div>

            <div class="complaint--form-inputs">
                <div class="complaint--form-input" :class="{'error' : validation.county}">
                    <label class="text-input-label" for="county">Megye *</label>
                    <select :disabled="disabled || loading" v-model="form.county" id="county">
                        <option value="" disabled>Válasszon megyét</option>
                        <option v-for="county in counties" :key="county" :value="county">
                            {{ county }}
                        </option>
                    </select>
                    <span class="input-error">{{ validation.county }}</span>
                </div>

                <div class="complaint--form-input" :class="{'error' : validation.zip}">
                    <label
                        class="text-input-label"
                        for="zip">
                        Irányítószám *
                    </label>
                    <input
                        ref="addressZip"
                        :disabled="disabled || loading"
                        v-model="form.zip"
                        @input="inputZip"
                        @focus="checkZip"
                        @blur="emptyCityList"
                        min="1000"
                        max="9999"
                        v-disable-mouse-wheel
                        v-autocomplete-off
                        id="zip"
                        type="number">
                    <span class="input-error">{{ validation.zip }}</span>
                    <ul class="dropdown-list" v-show="cityList.length > 0">
                        <li
                            v-for="(city, index) in cityList"
                            :key="index"
                            @click="writeCityName(city)">
                            {{ city }}
                        </li>
                    </ul>
                </div>

                <div class="complaint--form-input" :class="{'error' : validation.city}">
                    <label
                        class="text-input-label"
                        for="city">
                        Város *
                    </label>
                    <input
                        ref="addressCity"
                        v-model="form.city"
                        @input="inputCity"
                        @focus="checkCity"
                        @blur="blurCity"
                        :disabled="disabled || loading"
                        v-autocomplete-off
                        id="city"
                        type="text">
                    <span class="input-error">{{ validation.city }}</span>
                    <ul class="dropdown-list" v-show="zipList.length > 0">
                        <li
                            v-for="(zip, index) in zipList"
                            :key="index"
                            @click="writeZip(zip)">
                            {{ zip }}
                        </li>
                    </ul>
                </div>

                <div class="complaint--form-input" :class="{'error' : validation.street}">
                    <label
                        class="text-input-label"
                        for="street">
                        Utca *
                    </label>
                    <input :disabled="disabled || loading" v-model="form.street" id="street" type="text">
                    <span class="input-error">{{ validation.street }}</span>
                </div>

                <div class="complaint--form-input" :class="{'error' : validation.house_number}">
                    <label
                        class="text-input-label"
                        for="house_number">
                        Házszám *
                    </label>
                    <input :disabled="disabled || loading" v-model="form.house_number" id="house_number" type="text">
                    <span class="input-error">{{ validation.house_number }}</span>
                </div>
            </div>

            <div class="complaint--form-input" :class="{'error' : validation.installation_status}">
                <label
                    class="text-input-label"
                    for="installation_status">
                    Beépítés státusza *
                </label>
                <custom-filter
                    :large="true"
                    label=""
                    :dropdown-size="'auto'"
                    :value="form.installation_status"
                    @selected="(e) => {this.form.installation_status = e.id}"
                    :options="installationStatuses"
                    :disabled="disabled || loading"
                />
                <span class="input-error">{{ validation.installation_status }}</span>
            </div>

            <div v-if="form.installation_status !== 'before'" class="complaint--form-input">
                <div class="input-wrapper" :class="{'error' : validation.installation_date}">
                    <label class="text-input-label" for="address-data-form">Beépítés időpontja *</label>
                    <custom-datepicker
                        style="width:100%"
                        :format="'YYYY-MM-DD'"
                        :disabled="disabled || loading"
                        :enable-passed-dates="true"
                        :initDate="form.installation_date ? form.installation_date : null"
                        @changed="(el) => {form.installation_date = el}"
                    />
                    <span class="input-error">{{validation.installation_date}}</span>
                </div>
            </div>

            <div class="complaint--form-input" :class="{'error' : validation.delivery_note_number}">
                <label
                    class="text-input-label"
                    for="delivery_note_number">
                    Számla száma
                </label>
                <input
                    :disabled="disabled || loading"
                    v-model="form.delivery_note_number"
                    id="delivery_note_number"
                    type="text"
                >
                <span class="input-error">{{ validation.delivery_note_number }}</span>
            </div>

            <div v-if="edit == false || (admin == false && edit == true)" class="complaint--form-files">
                <div class="complaint--form-input" :class="{'error' : validation.delivery_note_files}">
                    <label class="text-input-label file--label"
                           for="delivery_note_files">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <title>progress-upload</title>
                            <path
                                d="M13,2.03C17.73,2.5 21.5,6.25 21.95,11C22.5,16.5 18.5,21.38 13,21.93V19.93C16.64,19.5 19.5,16.61 19.96,12.97C20.5,8.58 17.39,4.59 13,4.05V2.05L13,2.03M11,2.06V4.06C9.57,4.26 8.22,4.84 7.1,5.74L5.67,4.26C7.19,3 9.05,2.25 11,2.06M4.26,5.67L5.69,7.1C4.8,8.23 4.24,9.58 4.05,11H2.05C2.25,9.04 3,7.19 4.26,5.67M2.06,13H4.06C4.24,14.42 4.81,15.77 5.69,16.9L4.27,18.33C3.03,16.81 2.26,14.96 2.06,13M7.1,18.37C8.23,19.25 9.58,19.82 11,20V22C9.04,21.79 7.18,21 5.67,19.74L7.1,18.37M12,7.5L7.5,12H11V16H13V12H16.5L12,7.5Z"/>
                        </svg>
                        <span v-if="!form.delivery_note_files || form.delivery_note_files.length === 0">Számla PDF csatolása</span>
                        <span v-else>További számlák hozzáadása...</span>
                    </label>
                    <input class="hidden--input" @change="handleFileChange($event, 'delivery_note_files')" multiple
                           id="delivery_note_files" type="file" accept="image/jpeg, image/png, application/pdf">

                    <div class="file-list-container" v-if="form.delivery_note_files_previews && form.delivery_note_files_previews.length > 0">
                        <div class="file-list-item" v-for="(file, index) in form.delivery_note_files_previews" :key="index">
                            <img v-if="file.isImage" :src="file.url" class="file-preview-image" alt="preview"/>
                            <span v-else class="file-preview-icon">📄</span>
                            <span class="file-name" :title="file.name">{{ truncate(file.name, 20) }}</span>
                            <button @click="removeFile('delivery_note_files', index)" type="button" class="remove-file-btn">
                                <md-icon>delete</md-icon>
                            </button>
                        </div>
                    </div>

                    <span class="input-error">{{ validation.delivery_note_files }}</span>
                </div>
            </div>

            <div v-if="!edit" class="complaint--form-subtitle">
                Reklamációhoz csatolt dokumentumok
            </div>

            <div v-if="edit == false || (admin == false && edit == true)" class="complaint--form-files">
                <div class="complaint--form-input" :class="{'error' : validation.photo_of_dmg_files}">
                    <label class="text-input-label file--label"
                           for="photo_of_dmg_files">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <title>progress-upload</title>
                            <path
                                d="M13,2.03C17.73,2.5 21.5,6.25 21.95,11C22.5,16.5 18.5,21.38 13,21.93V19.93C16.64,19.5 19.5,16.61 19.96,12.97C20.5,8.58 17.39,4.59 13,4.05V2.05L13,2.03M11,2.06V4.06C9.57,4.26 8.22,4.84 7.1,5.74L5.67,4.26C7.19,3 9.05,2.25 11,2.06M4.26,5.67L5.69,7.1C4.8,8.23 4.24,9.58 4.05,11H2.05C2.25,9.04 3,7.19 4.26,5.67M2.06,13H4.06C4.24,14.42 4.81,15.77 5.69,16.9L4.27,18.33C3.03,16.81 2.26,14.96 2.06,13M7.1,18.37C8.23,19.25 9.58,19.82 11,20V22C9.04,21.79 7.18,21 5.67,19.74L7.1,18.37M12,7.5L7.5,12H11V16H13V12H16.5L12,7.5Z"/>
                        </svg>
                        <span v-if="!form.photo_of_dmg_files || form.photo_of_dmg_files.length === 0">Fotó a hibáról *</span>
                        <span v-else>További fotók hozzáadása...</span>
                    </label>
                    <input class="hidden--input" @change="handleFileChange($event, 'photo_of_dmg_files')" multiple
                           id="photo_of_dmg_files" type="file" accept="image/jpeg, image/png, application/pdf">

                    <div class="file-list-container" v-if="form.photo_of_dmg_files_previews && form.photo_of_dmg_files_previews.length > 0">
                        <div class="file-list-item" v-for="(file, index) in form.photo_of_dmg_files_previews" :key="index">
                            <img v-if="file.isImage" :src="file.url" class="file-preview-image" alt="preview"/>
                            <span v-else class="file-preview-icon">📄</span>
                            <span class="file-name" :title="file.name">{{ truncate(file.name, 20) }}</span>
                            <button @click="removeFile('photo_of_dmg_files', index)" type="button" class="remove-file-btn">
                                <md-icon>delete</md-icon>
                            </button>
                        </div>
                    </div>

                    <span class="input-error">{{ validation.photo_of_dmg_files }}</span>
                </div>

                <div class="complaint--form-input" :class="{'error' : validation.warranty_file}">
                    <label class="text-input-label file--label"
                           for="warranty_file">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <title>progress-upload</title>
                            <path
                                d="M13,2.03C17.73,2.5 21.5,6.25 21.95,11C22.5,16.5 18.5,21.38 13,21.93V19.93C16.64,19.5 19.5,16.61 19.96,12.97C20.5,8.58 17.39,4.59 13,4.05V2.05L13,2.03M11,2.06V4.06C9.57,4.26 8.22,4.84 7.1,5.74L5.67,4.26C7.19,3 9.05,2.25 11,2.06M4.26,5.67L5.69,7.1C4.8,8.23 4.24,9.58 4.05,11H2.05C2.25,9.04 3,7.19 4.26,5.67M2.06,13H4.06C4.24,14.42 4.81,15.77 5.69,16.9L4.27,18.33C3.03,16.81 2.26,14.96 2.06,13M7.1,18.37C8.23,19.25 9.58,19.82 11,20V22C9.04,21.79 7.18,21 5.67,19.74L7.1,18.37M12,7.5L7.5,12H11V16H13V12H16.5L12,7.5Z"/>
                        </svg>
                        <span v-if="!form.warranty_file || form.warranty_file.length === 0">Garancialevél PDF csatolása</span>
                        <span v-else>Garancialevél cseréje...</span>
                    </label>
                    <input class="hidden--input" @change="handleFileChange($event, 'warranty_file')"
                           id="warranty_file" type="file" accept="image/jpeg, image/png, application/pdf">

                    <div class="file-list-container" v-if="form.warranty_file_previews && form.warranty_file_previews.length > 0">
                        <div class="file-list-item" v-for="(file, index) in form.warranty_file_previews" :key="index">
                            <img v-if="file.isImage" :src="file.url" class="file-preview-image" alt="preview"/>
                            <span v-else class="file-preview-icon">📄</span>
                            <span class="file-name" :title="file.name">{{ truncate(file.name, 20) }}</span>
                            <button @click="removeFile('warranty_file', index)" type="button" class="remove-file-btn">
                                <md-icon>delete</md-icon>
                            </button>
                        </div>
                    </div>

                    <span class="input-error">{{ validation.warranty_file }}</span>
                </div>
            </div>

            <div class="complaint--form-input" :class="{'error' : validation.message}">
                <label
                    class="text-input-label"
                    for="message">
                    A Reklamáció részletes leírása *
                </label>
                <textarea :disabled="disabled || loading" v-model="form.message" id="message"></textarea>
                <span class="input-error">{{ validation.message }}</span>
            </div>

            <button :disabled="disabled" @click="submit" class="complaint--submit">
                <span v-if="!loading">{{ edit ? "Reklamáció frissítése" : "Reklamáció beküldése" }}</span>
                <span v-else>Küldés...</span>
            </button>
        </div>
    </div>
</template>

<script>
import {mapGetters} from 'vuex';
import {scrollToError} from '@/plugins/helpers.js';
import CustomFilter from "@/components/dropdowns/CustomFilter.vue";
import CustomDatepicker from "@/components/dropdowns/CustomDatepicker.vue";
import Search from '../components/Search.vue'
import CustomTable from '../components/CustomTable'

export default {
    name: "ComplaintQualityForm",
    props: {
        loading: Boolean,
        edit: Boolean,
        admin: {
            type: Boolean,
            required: false,
            default: false
        },
        form: Object,
        disabled: {
            type: Boolean,
            default: false
        }
    },
    components: {
        Search,
        CustomDatepicker,
        CustomFilter,
        CustomTable
    },
    emits: [
        'submit',
    ],
    data() {
        return {
            cityList: [],
            zipList: [],
            productLoading: false,
            search: '',
            searchTimer: null,
            productGroups: [],
            counties: [
                'Budapest',
                'Baranya',
                'Bács-Kiskun',
                'Békés',
                'Borsod-Abaúj-Zemplén',
                'Csongrád-Csanád',
                'Fejér',
                'Győr-Moson-Sopron',
                'Hajdú-Bihar',
                'Heves',
                'Jász-Nagykun-Szolnok',
                'Komárom-Esztergom',
                'Nógrád',
                'Pest',
                'Somogy',
                'Szabolcs-Szatmár-Bereg',
                'Tolna',
                'Vas',
                'Veszprém',
                'Zala'
            ],
            installationStatuses: [
                {id: 'before', name: 'Beépítés előtt'},
                {id: 'during', name: 'Beépítés közben'},
                {id: 'after', name: 'Beépítés után'}
            ],
            tableColumns: [
                {
                    label: this.$t('product.page.list.column.product_number'),
                    field: 'productNumber',
                },
                {
                    label: this.$t('product.page.list.column.name'),
                    field: 'name',
                },
                {
                    label: this.$t('product.page.list.column.type'),
                    field: 'type',
                },
            ]
        }
    },
    computed: {
        ...mapGetters(['validation', 'productListPages', 'productListMeta', 'zipCodesList', 'citiesList',]),
        searchData() {
            return {
                placeholder: this.$t('product.page.list.search.placeholder'),
                tooltipText: this.$t('product.page.list.search.tooltip'),
                countText: this.$tc('product.page.list.search.name', this.productListMeta.total)
            }
        },
    },
    mounted() {
        this.$store.dispatch('FETCH_ZIPCODE_LIST')
        this.$store.dispatch('FETCH_CITY_LIST')
    },
    methods: {
        writeCityName(name) {
            this.$refs.addressCity.setAttribute('tabindex', '-1');
            this.form.city = name;
            this.emptyZipList()
        },

        handleFileChange(event, fieldName) {
            const newFiles = event.target.files;
            if (!newFiles || newFiles.length === 0) {
                return;
            }

            const MAX_SIZE_MB = 5;
            const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

            for (let i = 0; i < newFiles.length; i++) {
                if (newFiles[i].size > MAX_SIZE_BYTES) {
                    alert(`A fájl túl nagy: ${newFiles[i].name}. A maximális méret: ${MAX_SIZE_MB}MB`);
                    event.target.value = null;
                    return;
                }
            }

            const fileArrayName = fieldName;
            const previewArrayName = fieldName + "_previews";

            const isSingleFile = (fieldName === 'warranty_file');

            if (!Array.isArray(this.form[fileArrayName])) {
                this.form[fileArrayName] = [];
            }
            if (!Array.isArray(this.form[previewArrayName])) {
                this.$set(this.form, previewArrayName, []);
            }

            if (isSingleFile) {
                this.form[fileArrayName] = [];
                this.form[previewArrayName] = [];
            }

            const newFilesArray = Array.from(newFiles);
            this.form[fileArrayName].push(...newFilesArray);

            newFilesArray.forEach(file => {
                const isImage = file.type.startsWith('image/');
                const isPdf = file.type === 'application/pdf';

                if (isImage || isPdf) {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                        this.form[previewArrayName].push({
                            url: e.target.result,
                            name: file.name,
                            isImage: isImage
                        });
                    };
                    reader.readAsDataURL(file);
                } else {
                    this.form[previewArrayName].push({
                        url: null,
                        name: file.name,
                        isImage: false
                    });
                }
            });

            event.target.value = null;
        },

        removeFile(fieldName, index) {
            const fileArrayName = fieldName;
            const previewArrayName = fieldName + "_previews";

            if (this.form[fileArrayName] && typeof this.form[fileArrayName][index] !== 'undefined') {
                this.form[fileArrayName].splice(index, 1);
            }
            if (this.form[previewArrayName] && typeof this.form[previewArrayName][index] !== 'undefined') {
                this.form[previewArrayName].splice(index, 1);
            }
        },

        truncate(text, length) {
            if (text.length > length)
                return text.substring(0, length) + '...';
            else
                return text;
        },

        loadPage(page) {
            this.productLoading = true
            this.$store.dispatch('SEARCH_COMPLAINT_PRODUCTS', {complaintType: 'quality', page: page, keepOtherPages: true}).then(() => {
                this.productLoading = false
            })
        },

        onRowClick(params) {
            this.form.products.push({
                product_name: params.row.name,
                product_number: params.row.productNumber,
                product_unit: params.row.sapUnit,
                product_qty: 1,
                product_price: params.row.price
            });
        },

        onSearchPhraseChanged(phrase) {

            if(phrase.length === 0){
                this.$store.commit('resetProducts')
            }

            if (this.searchTimer) {
                clearTimeout(this.searchTimer)
                this.searchTimer = null
            }

            if (this.search === phrase) {
                return
            }

            this.searchTimer = setTimeout(() => {
                this.searchTimer = null
                this.$store.commit('setProductListActiveFilters', {
                    ...this.productListActiveFilters,
                    search: phrase.length > 0 ? phrase : void 0
                })
                this.refresh()
            }, 300)
        },

        removePage(page) {
            this.$store.commit('removeProductListPage', page)
        },

        refresh() {
            this.productLoading = true
            this.$store.dispatch('SEARCH_COMPLAINT_PRODUCTS', {complaintType: 'quality', page: 1, keepOtherPages: false}).then(() => {
                this.productLoading = false
            })
        },

        inputZip() {
            // eslint-disable-next-line vue/no-mutating-props
            this.form.city = ''
            this.cityList = []
            this.checkZip()
        },

        checkZip() {
            this.$refs.addressZip.removeAttribute('tabindex')

            let zip = String(this.form.zip)
            if (zip && zip.length > 4) {
                // eslint-disable-next-line vue/no-mutating-props
                this.form.zip = Number((String(this.form.zip)).substring(0, 4))
            }

            if (this.form.zip !== null && String(this.form.zip).length === 4) {
                this.setCity()
            } else {
                this.cityList = []
            }
        },

        inputCity() {
            // eslint-disable-next-line vue/no-mutating-props
            this.form.zip = ''
            this.zipList = []
            this.checkCity()
        },

        checkCity() {
            this.$refs.addressCity.removeAttribute('tabindex')

            if (this.city !== null && String(this.city).length > 2) {
                this.setZip()
            } else {
                this.zipList = []
            }
        },

        blurCity() {
            this.citiesList.forEach(el => {
                if (el.name.toLowerCase() === this.form.city.toLowerCase() && el.name !== this.form.city) {
                    // eslint-disable-next-line vue/no-mutating-props
                    this.form.city = el.name
                }
            })
            this.emptyZipList()
        },

        emptyCityList() {
            let that = this
            setTimeout(function () {
                if (that.cityList.length > 0) {
                    that.cityList = []
                }
            }, 250)
        },

        emptyZipList() {
            let that = this
            setTimeout(function () {
                if (that.zipList.length > 0) {
                    that.zipList = []
                }
            }, 250)
        },

        setCity() {
            this.zipCodesList.forEach(el => {
                const sameZip = Number(el.zip) === Number(this.form.zip)
                if (sameZip && el.cities.length === 1) {
                    this.$refs.addressCity.setAttribute('tabindex', '-1')
                    // eslint-disable-next-line vue/no-mutating-props
                    this.form.city = el.cities[0]
                } else if (sameZip && el.cities.length > 1) {
                    this.cityList = el.cities
                }
            })
        },

        setZip() {
            this.citiesList.forEach(el => {
                if (el.name.toLowerCase() === this.form.city.toLowerCase()) {
                    if (el.zips.length === 1) {
                        this.$refs.addressZip.setAttribute('tabindex', '-1')
                        // eslint-disable-next-line vue/no-mutating-props
                        this.form.zip = el.zips[0]
                    } else {
                        this.zipList = el.zips
                    }
                }
            })
        },

        submit() {
            this.$emit('submit')
        }
    },
    watch: {
        validation: {
            handler: function (val) {
                if (val && val['photo_of_dmg_files.0']) {
                    this.validation.photo_of_dmg_files = val['photo_of_dmg_files.0']
                }

                if (val && val['delivery_note_files.0']) {
                    this.validation.delivery_note_files = val['delivery_note_files.0']
                }

                scrollToError();
            }
        }
    },
}
</script>
<style lang="scss" scoped>
.file-list-container {
    margin-top: 10px;
    width: 100%;
}

.file-list-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px;
    border: 1px solid #eee;
    border-radius: 4px;
    margin-bottom: 5px;
    background-color: #f9f9f9;
    overflow: hidden;
}

.file-preview-image {
    width: 40px;
    height: 40px;
    object-fit: cover;
    border-radius: 4px;
    margin-right: 10px;
    flex-shrink: 0;
}

.file-preview-icon {
    font-size: 24px;
    margin-right: 10px;
}

.file-name {
    flex-grow: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 14px;
    margin-right: 10px;
    min-width: 0;
}

.remove-file-btn {
    background: none;
    border: none;
    cursor: pointer;
    color: #cc0000;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.remove-file-btn .md-icon {
    font-size: 20px !important;
}

.file--label {
    cursor: pointer;
}
</style>
